<template>
  <div class="pm-page so-page">
    <!-- Hero: search, quick actions and how many systems are complete (US 9370) -->
    <section class="home-hero pm-hero">
      <div class="home-hero__main">
        <div class="home-hero__eyebrow">{{ $t('systems_overview.eyebrow', { name: getClient.name }) }}</div>
        <h1 class="home-hero__title">{{ $t('systems.title') }}</h1>
        <p class="home-hero__summary">{{ $t('systems_overview.intro') }}</p>

        <div class="home-search pm-search">
          <font-awesome-icon icon="search" class="home-search__icon"/>
          <input
              ref="search"
              v-model="query"
              class="home-search__input"
              :placeholder="$t('systems_overview.search_placeholder')"
              :aria-label="$t('systems_overview.search_placeholder')"
              autocomplete="off"
              qid="systems-search"
          />
          <span class="home-kbd">{{ isMac ? '⌘K' : 'Ctrl+K' }}</span>
        </div>

        <div class="home-hero__chips pm-hero__chips">
          <button v-if="isGranted(permissions.SYSTEM_CREATE)"
              type="button"
              class="home-chip home-chip--light"
              qid="create-s-button"
              @click="$refs.create.show()">
            <font-awesome-icon icon="plus"/>{{ $t('systems_overview.actions.add') }}
          </button>
          <router-link v-if="isGranted(permissions.SYSTEM_CREATE)"
              :to="{ name: 'systems.import' }"
              class="home-chip"
              qid="systems-import">
            <font-awesome-icon icon="cloud-upload-alt"/>{{ $t('systems_overview.actions.import') }}
          </router-link>
          <button v-if="getClient.projectModule"
              type="button"
              class="home-chip"
              qid="systems-get-updated"
              @click="startProject">
            <font-awesome-icon icon="sync-alt"/>{{ $t('systems_overview.actions.get_updated') }}
          </button>
          <button v-if="isGranted(permissions.MAINTENANCE_VIEW)"
              type="button"
              class="home-chip"
              qid="download-table-button"
              @click="$refs.actions.openDownload()">
            <font-awesome-icon icon="file-download"/>{{ $t('systems_overview.actions.export') }}
          </button>
        </div>
      </div>

      <div v-if="inUse.length" class="home-week pm-progress">
        <div class="home-week__top">
          <svg class="home-ring"
              width="96"
              height="96"
              viewBox="0 0 96 96"
              aria-hidden="true">
            <circle class="home-ring__track"
                cx="48"
                cy="48"
                r="42"/>
            <circle v-if="completeCount"
                class="home-ring__value"
                cx="48"
                cy="48"
                r="42"
                :stroke-dasharray="ringDash(completeCount, inUse.length, 42)"/>
          </svg>
          <b class="home-week__count pm-progress__count">{{ completeCount }}/{{ inUse.length }}</b>
          <div>
            <div class="home-week__title">{{ $t('systems_overview.progress.title') }}</div>
            <div class="home-week__text">{{ $t('systems_overview.progress.text') }}</div>
          </div>
        </div>
        <div class="home-week__stats">
          <div><b>{{ inUse.length }}</b>{{ $t('systems_overview.progress.in_use') }}</div>
          <div><b>{{ counts.nobody }}</b>{{ $t('systems_overview.progress.nobody') }}</div>
          <div v-if="overview.modules.projects"><b>{{ counts.tasks }}</b>{{ $t('systems_overview.progress.tasks') }}</div>
        </div>
      </div>
    </section>

    <!-- Needs attention -->
    <div v-if="attention.length" class="pm-attention">
      <button
          v-for="item in attention"
          :key="item.key"
          type="button"
          class="pm-att"
          :qid="'systems-attention-' + item.key"
          @click="openAttention(item)"
      >
        <span class="pm-att__dot" :class="'is-' + item.color"></span>
        <span class="pm-att__text">
          <b>{{ item.title }}</b>
          <small>{{ item.text }}</small>
        </span>
        <span class="v2-link">{{ item.action }}</span>
      </button>
    </div>

    <!-- Inventory -->
    <section class="v2-card so-card">
      <div class="pm-map__head">
        <div class="pm-map__titles">
          <h3>{{ $t('systems_overview.inventory.title') }}</h3>
          <span class="v2-card__sub">{{ $t('systems_overview.inventory.subtitle') }}</span>
        </div>
        <div v-if="lenses.length > 1" class="v2-seg" role="tablist">
          <span v-for="item in lenses"
              :key="item"
              role="tab"
              :class="{ on: lens === item }"
              :qid="'systems-lens-' + item"
              @click="setLens(item)">{{ $t('systems_overview.lens.' + item) }}</span>
        </div>
      </div>

      <div class="so-tabs-row">
        <div class="v2-seg" role="tablist">
          <span v-for="item in tabs"
              :key="item.key"
              role="tab"
              :class="{ on: tab === item.key }"
              :qid="'systems-tab-' + item.key"
              @click="setTab(item.key)">
            {{ $t('systems_overview.tabs.' + item.key) }}
            <i class="pm-tabs__count" :class="{ 'is-warn': item.warn && item.count }">{{ item.count }}</i>
          </span>
        </div>
      </div>

      <!-- Bulk actions -->
      <div v-if="selected.length" class="so-bulk">
        <b>{{ $tc('systems_overview.bulk.selected', selected.length, { count: selected.length }) }}</b>
        <b-dropdown variant="link" toggle-class="so-bulk__btn" no-caret @shown="loadUsers('')" qid="systems-bulk-owner">
          <template #button-content><font-awesome-icon icon="user"/>{{ $t('systems_overview.bulk.owner') }}</template>
          <b-dropdown-form @submit.prevent>
            <b-form-input v-model="userQuery" size="sm" :placeholder="$t('systems_overview.owner.search')" @input="loadUsers(userQuery)"></b-form-input>
          </b-dropdown-form>
          <b-dropdown-item v-for="user in users" :key="user.id" @click="bulk('owner', { owner: user.id })">{{ user.name }}</b-dropdown-item>
        </b-dropdown>
        <button type="button" class="so-bulk__btn" qid="systems-bulk-contributors" @click="openBulkModal('contributors')">
          <font-awesome-icon icon="users"/>{{ $t('systems_overview.bulk.contributors') }}
        </button>
        <button type="button" class="so-bulk__btn" qid="systems-bulk-tags" @click="openBulkModal('tags')">
          <font-awesome-icon icon="tag"/>{{ $t('systems_overview.bulk.tags') }}
        </button>
        <b-dropdown variant="link" toggle-class="so-bulk__btn" no-caret qid="systems-bulk-runs-as">
          <template #button-content><font-awesome-icon icon="database"/>{{ $t('systems_overview.bulk.runs_as') }}</template>
          <b-dropdown-item v-for="value in runsAsValues" :key="value" @click="bulk('runs_as', { runs_as: value })">{{ $t('systems_overview.runs_as.' + value) }}</b-dropdown-item>
        </b-dropdown>
        <button v-if="tab !== 'retired'" type="button" class="so-bulk__btn" qid="systems-bulk-retire" @click="bulk('retire')">
          <font-awesome-icon icon="archive"/>{{ $t('systems_overview.bulk.retire') }}
        </button>
        <button v-else type="button" class="so-bulk__btn" qid="systems-bulk-reactivate" @click="bulk('reactivate')">
          <font-awesome-icon icon="rotate-left"/>{{ $t('systems_overview.bulk.reactivate') }}
        </button>
        <button type="button" class="so-bulk__close" :aria-label="$t('systems_overview.bulk.clear')" @click="selected = []">
          <font-awesome-icon icon="times"/>
        </button>
      </div>

      <div ref="table" class="so-table">
        <div class="so-row so-row--head" :style="gridStyle">
          <span><input type="checkbox" class="so-check" :checked="allSelected" :aria-label="$t('systems_overview.bulk.select_all')" @change="toggleAll($event.target.checked)"/></span>
          <span>{{ $t('systems_overview.columns.system') }}</span>
          <span v-for="column in columns" :key="column">{{ $t('systems_overview.columns.' + column) }}</span>
          <span></span>
        </div>

        <div v-if="loading" class="home-empty">{{ $t('systems_overview.loading') }}</div>
        <template v-else>
          <div v-for="system in rows"
              :key="system.id"
              class="so-row"
              :class="{ 'is-dim': system.lifecycle_status === 'retired', 'is-selected': selected.includes(system.id) }"
              :style="gridStyle">
            <span>
              <input v-if="canEdit(system)"
                  type="checkbox"
                  class="so-check"
                  :checked="selected.includes(system.id)"
                  :aria-label="system.name"
                  @change="toggle(system.id, $event.target.checked)"/>
            </span>
            <span class="so-name">
              <span v-if="system.logo" class="so-logo has-image"><img :src="system.logo" alt=""></span>
              <span v-else class="so-logo" :style="{ background: logoColor(system.name) }">{{ initials(system.name) }}</span>
              <span class="so-name__text">
                <router-link :to="{ name: 'systems.view', params: { slug: system.slug } }" class="so-name__link" qid="s-view-page-link">{{ system.name }}</router-link>
                <small v-if="system.from_workspace" class="so-from" :title="$t('systems_overview.from_workspace_hint')"><font-awesome-icon icon="lock"/>{{ $t('systems_overview.from_workspace', { name: system.from_workspace }) }}</small>
                <small v-else>{{ subline(system) }}</small>
              </span>
            </span>

            <span v-for="column in columns" :key="column" class="so-cell">
              <!-- Owner, editable in place -->
              <template v-if="column === 'owner'">
                <b-dropdown v-if="canEdit(system)"
                    variant="link"
                    toggle-class="so-inline"
                    no-caret
                    :qid="'systems-owner-' + system.slug"
                    @shown="loadUsers('')">
                  <template #button-content>
                    <span v-if="system.owner" class="so-owner"><span class="so-avatar">{{ initials(system.owner.name) }}</span><span class="so-owner__name">{{ system.owner.name }}</span></span>
                    <span v-else class="so-assign">
                      <span class="so-chip so-chip--miss"><font-awesome-icon icon="plus"/>{{ $t('systems_overview.owner.assign') }}</span>
                      <small v-if="system.created_by_name && !system.members.length">{{ $t('systems_overview.owner.added_by', { name: system.created_by_name }) }}</small>
                    </span>
                  </template>
                  <b-dropdown-form @submit.prevent>
                    <b-form-input v-model="userQuery" size="sm" :placeholder="$t('systems_overview.owner.search')" @input="loadUsers(userQuery)"></b-form-input>
                  </b-dropdown-form>
                  <b-dropdown-item v-for="user in users" :key="user.id" :active="system.owner && system.owner.id === user.id" @click="setOwner(system, user)">{{ user.name }}</b-dropdown-item>
                  <b-dropdown-text class="so-hint">{{ $t('systems_overview.owner.hint') }}</b-dropdown-text>
                </b-dropdown>
                <span v-else-if="system.owner" class="so-owner"><span class="so-avatar">{{ initials(system.owner.name) }}</span><span class="so-owner__name">{{ system.owner.name }}</span></span>
                <span v-else class="so-muted">{{ $t('systems_overview.owner.none') }}</span>
              </template>

              <!-- Where it runs, editable in place -->
              <template v-else-if="column === 'runs_in'">
                <b-dropdown v-if="canEdit(system)" variant="link" toggle-class="so-inline" no-caret :qid="'systems-runs-as-' + system.slug">
                  <template #button-content>
                    <span v-if="system.runs_as" class="so-two">{{ $t('systems_overview.runs_as.' + system.runs_as) }}<small>{{ where(system) }}</small></span>
                    <span v-else class="so-chip so-chip--miss"><font-awesome-icon icon="plus"/>{{ $t('systems_overview.runs_as.set') }}</span>
                  </template>
                  <b-dropdown-item v-for="value in runsAsValues" :key="value" :active="system.runs_as === value" @click="setRunsAs(system, value)">{{ $t('systems_overview.runs_as.' + value) }}</b-dropdown-item>
                </b-dropdown>
                <span v-else class="so-two">{{ system.runs_as ? $t('systems_overview.runs_as.' + system.runs_as) : '–' }}<small>{{ where(system) }}</small></span>
              </template>

              <template v-else-if="column === 'data'">
                <span class="so-chips">
                  <span v-for="name in system.data_categories.slice(0, 2)" :key="name" class="so-chip" :class="{ 'so-chip--sens': system.sensitive_data_categories.includes(name) }">{{ name }}</span>
                  <span v-if="system.data_categories.length > 2" class="so-more" :title="system.data_categories.slice(2).join(', ')">+{{ system.data_categories.length - 2 }}</span>
                  <span v-if="!system.data_categories.length" class="so-muted">–</span>
                </span>
              </template>

              <template v-else-if="column === 'used'">
                <span class="so-chips">
                  <span v-if="overview.modules.gdpr && system.activity_count" class="so-use so-use--gdpr"><font-awesome-icon icon="project-diagram"/>{{ $tc('systems_overview.used.activities', system.activity_count, { count: system.activity_count }) }}</span>
                  <span v-if="overview.modules.contracts && system.contracts.length" class="so-use"><font-awesome-icon icon="file-signature"/>{{ $tc('systems_overview.used.contracts', system.contracts.length, { count: system.contracts.length }) }}</span>
                  <span v-if="overview.modules.risk && system.risk_count" class="so-use so-use--grc"><font-awesome-icon icon="shield-alt"/>{{ $tc('systems_overview.used.risks', system.risk_count, { count: system.risk_count }) }}</span>
                  <span v-if="isUnused(system)" class="so-use so-use--none">{{ $t('systems_overview.used.none') }}</span>
                </span>
              </template>

              <template v-else-if="column === 'criticality'">
                <span class="so-cia">
                  <b v-for="key in ['confidentiality', 'integrity', 'availability']"
                      :key="key"
                      :class="ciaClass(system.criticality[key])"
                      :title="$t('systems_overview.criticality.' + key) + (system.criticality[key] ? ': ' + $t('impact.' + impactKey(system.criticality[key])) : '')">{{ impactLevel(system.criticality[key]) || '–' }}</b>
                </span>
              </template>

              <template v-else-if="column === 'recovery'">
                <span v-if="system.rto || system.rpo" class="so-two">
                  {{ system.rto ? 'RTO ' + system.rto.number + ' ' + system.rto.type : '–' }}
                  <small>{{ system.rpo ? 'RPO ' + system.rpo.number + ' ' + system.rpo.type : '' }}</small>
                </span>
                <span v-else class="so-muted">{{ $t('systems_overview.recovery.none') }}</span>
              </template>

              <template v-else-if="column === 'risks'">
                <span class="so-use so-use--grc">{{ $tc('systems_overview.used.risks', system.risk_count, { count: system.risk_count }) }}</span>
              </template>

              <template v-else-if="column === 'confirmed'">
                <span class="so-two" :class="confirmedClass(system)">
                  {{ confirmedText(system) }}
                  <small>{{ confirmedSub(system) }}</small>
                </span>
              </template>

              <template v-else-if="column === 'status'">
                <span v-if="system.lifecycle_status === 'retired'" class="pm-status">{{ $t('systems_overview.status.retired') }}</span>
                <span v-else-if="!system.missing.length" class="pm-status is-ok"><font-awesome-icon icon="check"/>{{ $t('systems_overview.status.complete') }}</span>
                <span v-else class="pm-status is-warn" :title="missingTitle(system)"><font-awesome-icon icon="exclamation-triangle"/>{{ $tc('systems_overview.status.missing', system.missing.length, { count: system.missing.length }) }}</span>
              </template>
            </span>

            <span class="so-kebab">
              <b-dropdown variant="link" toggle-class="so-kebab__btn" no-caret right :qid="'systems-row-menu-' + system.slug">
                <template #button-content><font-awesome-icon icon="ellipsis-h"/></template>
                <b-dropdown-item :to="{ name: 'systems.view', params: { slug: system.slug } }">{{ $t('systems_overview.row.open') }}</b-dropdown-item>
                <b-dropdown-item v-if="canDelete(system)" qid="delete-s-modal" @click="$refs.actions.showConfirmDeleteModal(system)">{{ $t('systems_overview.row.delete') }}</b-dropdown-item>
              </b-dropdown>
            </span>
          </div>
          <div v-if="!rows.length" class="home-empty">{{ query ? $t('systems_overview.empty_search') : $t('systems_overview.empty_tab') }}</div>
        </template>
      </div>
      <div v-if="!loading" class="pm-foot so-foot">
        <span><span class="so-use so-use--gdpr">GDPR</span><span class="so-use so-use--grc">GRC</span>{{ $t('systems_overview.legend.modules') }}</span>
        <span v-if="hiddenColumns" class="so-foot__hidden">{{ $tc('systems_overview.columns_hidden', hiddenColumns, { count: hiddenColumns }) }}</span>
        <span class="so-foot__count">{{ $t('systems_overview.showing', { shown: rows.length, total: tabRows.length }) }}</span>
      </div>
    </section>

    <!-- Contributors or tags for the selected systems -->
    <b-modal ref="bulk-modal" hide-footer @hidden="bulkValues = []">
      <div class="d-block text-center">
        <h4>{{ bulkMode ? $t('systems_overview.bulk.' + bulkMode + '_title') : '' }}</h4>
      </div>
      <b-form-group>
        <multiselect v-if="bulkMode === 'contributors'"
            v-model="bulkValues"
            :options="users"
            :multiple="true"
            track-by="id"
            label="name"
            :select-label="$t('system.dropdown_select_label')"
            :selected-label="$t('system.dropdown_selected_label')"
            :deselect-label="$t('system.dropdown_deselect_label')"
            :placeholder="$t('systems_overview.owner.search')"
            qid="systems-bulk-contributors-select"
            @search-change="loadUsers"
        ></multiselect>
        <multiselect v-else
            v-model="bulkValues"
            :options="tags"
            :multiple="true"
            :taggable="true"
            track-by="name_translated"
            label="name_translated"
            :select-label="$t('system.dropdown_select_label')"
            :selected-label="$t('system.dropdown_selected_label')"
            :deselect-label="$t('system.dropdown_deselect_label')"
            :placeholder="$t('systems.create.tags_placeholder')"
            qid="systems-bulk-tags-select"
            @search-change="loadTags"
            @tag="addTag"
        ></multiselect>
      </b-form-group>
      <div class="text-right">
        <b-button class="mt-3" variant="secondary" @click="$refs['bulk-modal'].hide()">{{ $t('systems.delete.cancel') }}</b-button>
        <b-button class="mt-3 ml-3"
            variant="success"
            :disabled="!bulkValues.length"
            qid="systems-bulk-submit"
            @click="submitBulkModal">
          {{ $t('systems_overview.bulk.apply') }}
        </b-button>
      </div>
    </b-modal>

    <span class="pm-hidden-trigger">
      <CreateProjectModal v-if="getClient.projectModule"
          ref="createProject"
          type="update"
          :types="['update', 'validation']"
          scope="systems"></CreateProjectModal>
    </span>
    <SystemListActions ref="actions" @deleted="loadOverview"></SystemListActions>
    <SystemCreatePanel ref="create" @created="loadOverview"></SystemCreatePanel>
  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import {impactClass, impactLevel, impactKey} from '@/util/impactLevels.js';
  import moment from 'moment';
  import CreateProjectModal from '@/views/Projects/CreateProjectModal.vue';
  import SystemListActions from '@/components/systems/SystemListActions.vue';
  import SystemCreatePanel from '@/views/Systems/page/SystemCreatePanel.vue';

  const RUNS_AS = ['premises', 'hosted', 'saas', 'physical'];
  // Smallest width and share of the free space per column; the table drops columns (DROP_ORDER first) that don't fit
  const WIDTHS = {
    owner: [140, 1.1], runs_in: [110, 1], data: [130, 1.2], used: [130, 1.2], confirmed: [118, 1],
    status: [104, 0], criticality: [90, .8], recovery: [96, .8], risks: [96, .8]
  };
  const DROP_ORDER = ['data', 'runs_in', 'confirmed', 'recovery', 'criticality', 'risks', 'used'];
  const NAME_WIDTH = 180;
  const FIXED_WIDTH = 22 + 28 + 40;
  const GAP = 14;
  const LOGO_COLORS = ['#1F7A72', '#3B6FD8', '#7B5CD6', '#D9532B', '#0E7C86', '#C27C00', '#5B6573', '#C2417A'];
  const STORAGE_KEY = 'systems-overview';

  export default {
    name: 'Systems',
    components: {
      CreateProjectModal,
      SystemListActions,
      SystemCreatePanel
    },
    data: function() {
      return {
        pendingCreate: false,
        overview: { modules: {}, systems: [], attention: {} },
        loading: true,
        query: '',
        tab: 'in_use',
        lens: 'privacy',
        tableWidth: 0,
        selected: [],
        users: [],
        userQuery: '',
        tags: [],
        bulkMode: '',
        bulkValues: [],
        runsAsValues: RUNS_AS,
        isMac: typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      inUse() {
        return this.overview.systems.filter(system => system.lifecycle_status !== 'retired');
      },
      completeCount() {
        return this.inUse.filter(system => !system.missing.length).length;
      },
      counts() {
        return {
          nobody: this.inUse.filter(this.isNobody).length,
          missing: this.inUse.filter(system => system.missing.length).length,
          tasks: this.inUse.filter(system => system.open_task).length,
          unused: this.inUse.filter(this.isUnused).length,
          retired: this.overview.systems.length - this.inUse.length
        };
      },
      tabs() {
        let tabs = [
          { key: 'in_use', count: this.inUse.length },
          { key: 'nobody', count: this.counts.nobody, warn: true },
          { key: 'missing', count: this.counts.missing, warn: true }
        ];
        if (this.overview.modules.projects) {
          tabs.push({ key: 'tasks', count: this.counts.tasks });
        }
        if (this.overview.modules.gdpr) {
          tabs.push({ key: 'unused', count: this.counts.unused });
        }
        tabs.push({ key: 'retired', count: this.counts.retired });
        return tabs;
      },
      // Privacy needs GDPR, Risk needs the risk module; Security is always there
      lenses() {
        let lenses = [];
        if (this.overview.modules.gdpr) {
          lenses.push('privacy');
        }
        lenses.push('security');
        if (this.overview.modules.risk) {
          lenses.push('risk');
        }
        return lenses;
      },
      activeLens() {
        return this.lenses.includes(this.lens) ? this.lens : this.lenses[0];
      },
      lensColumns() {
        let modules = this.overview.modules;
        let columns = {
          privacy: ['owner', 'runs_in', 'data', 'used', 'confirmed', 'status'],
          security: ['owner', 'runs_in', 'criticality', 'recovery', 'confirmed', 'status'],
          risk: ['owner', 'criticality', 'risks', 'used', 'status']
        }[this.activeLens];
        return columns.filter(column => column !== 'confirmed' || modules.projects);
      },
      // The lens's columns that fit the table's width; owner and status always stay
      columns() {
        let columns = this.lensColumns.slice();
        let needed = list => FIXED_WIDTH + NAME_WIDTH + list.reduce((sum, column) => sum + WIDTHS[column][0], 0) + (list.length + 3) * GAP;
        let drop = DROP_ORDER.filter(column => columns.includes(column));
        while (this.tableWidth && needed(columns) > this.tableWidth && drop.length) {
          let column = drop.shift();
          columns = columns.filter(item => item !== column);
        }
        return columns;
      },
      hiddenColumns() {
        return this.lensColumns.length - this.columns.length;
      },
      gridStyle() {
        let columns = this.columns.map(column => {
          let [min, share] = WIDTHS[column];
          return share ? 'minmax(' + min + 'px, ' + share + 'fr)' : min + 'px';
        });
        return { gridTemplateColumns: '22px minmax(' + NAME_WIDTH + 'px, 1.5fr) ' + columns.join(' ') + ' 28px' };
      },
      tabRows() {
        let filters = {
          in_use: system => system.lifecycle_status !== 'retired',
          nobody: system => system.lifecycle_status !== 'retired' && this.isNobody(system),
          missing: system => system.lifecycle_status !== 'retired' && system.missing.length,
          tasks: system => system.lifecycle_status !== 'retired' && system.open_task,
          unused: system => system.lifecycle_status !== 'retired' && this.isUnused(system),
          retired: system => system.lifecycle_status === 'retired'
        };
        return this.overview.systems.filter(filters[this.tab] || filters.in_use);
      },
      rows() {
        let query = this.query.trim().toLowerCase();
        if (!query) {
          return this.tabRows;
        }
        return this.tabRows.filter(system => [system.name, system.owner ? system.owner.name : '', system.location]
          .concat(system.data_categories || [], (system.contracts || []).map(contract => contract.name))
          .some(value => value && value.toLowerCase().includes(query)));
      },
      selectableRows() {
        return this.rows.filter(this.canEdit);
      },
      allSelected() {
        return this.selectableRows.length > 0 && this.selectableRows.every(system => this.selected.includes(system.id));
      },
      attention() {
        let items = [];
        let attention = this.overview.attention || {};
        if (attention.nobody_assigned) {
          items.push({ key: 'nobody', color: 'amber', tab: 'nobody',
            title: this.$tc('systems_overview.attention.nobody_title', attention.nobody_assigned, { count: attention.nobody_assigned }),
            text: this.$t('systems_overview.attention.nobody_text'), action: this.$t('systems_overview.attention.assign') });
        }
        if (attention.overdue_tasks && attention.overdue_tasks.length) {
          let task = attention.overdue_tasks[0];
          items.push({ key: 'overdue', color: 'red', slug: task.slug,
            title: this.$t('systems_overview.attention.overdue_title', { name: task.name }),
            text: task.owner ? this.$t('systems_overview.attention.overdue_text', { project: task.project, owner: task.owner }) : task.project,
            action: this.$t('systems_overview.attention.open') });
        }
        if (attention.unsorted_links) {
          items.push({ key: 'unsorted', color: 'blue', route: { name: 'systems.contract_links' },
            title: this.$tc('systems_overview.attention.unsorted_title', attention.unsorted_links, { count: attention.unsorted_links }),
            text: this.$t('systems_overview.attention.unsorted_text'), action: this.$t('systems_overview.attention.sort') });
        }
        if (attention.runs_as_missing) {
          items.push({ key: 'runs_as', color: 'blue', tab: 'missing',
            title: this.$tc('systems_overview.attention.runs_as_title', attention.runs_as_missing, { count: attention.runs_as_missing }),
            text: this.overview.modules.contracts ? this.$t('systems_overview.attention.runs_as_text') : this.$t('systems_overview.attention.runs_as_text_basic'),
            action: this.$t('systems_overview.attention.set') });
        }
        if (attention.not_used) {
          items.push({ key: 'unused', color: 'blue', tab: 'unused',
            title: this.$tc('systems_overview.attention.unused_title', attention.not_used, { count: attention.not_used }),
            text: this.$t('systems_overview.attention.unused_text'), action: this.$t('systems_overview.attention.review') });
        }
        return items.slice(0, 4);
      }
    },
    watch: {
      getClient: function() {
        this.selected = [];
        this.loadOverview();
      },
      tab() {
        this.selected = [];
      }
    },
    methods: {
      loadOverview() {
        let self = this;
        if (!this.getClient || !this.getClient.slug) {
          return;
        }
        piincHttp.get('systems/overview', { params: { client: this.getClient.slug } }).then(function(response) {
          self.overview = response.data;
          self.loading = false;
          self.selected = self.selected.filter(id => _.find(self.overview.systems, { id: id }));
          if (self.pendingCreate) {
            self.pendingCreate = false;
            history.replaceState(null, '', self.$router.resolve({ name: 'systems' }).href);
            if (self.isGranted(self.permissions.SYSTEM_CREATE)) {
              self.$refs.create.show();
            }
          }
        }, function() {
          self.loading = false;
        });
      },
      isNobody(system) {
        return !system.owner && !system.members.length;
      },
      isUnused(system) {
        let modules = this.overview.modules;
        return modules.gdpr && !system.activity_count && (!modules.contracts || !system.contracts.length) && (!modules.risk || !system.risk_count);
      },
      canEdit(system) {
        return this.isGranted([this.permissions.SYSTEM_EDIT, this.permissions.SYSTEM_EDIT_OWN], system) && (!this.getClient.parent || system.partition);
      },
      canDelete(system) {
        return this.isGranted([this.permissions.SYSTEM_DELETE, this.permissions.SYSTEM_DELETE_OWN], system) && (!this.getClient.parent || system.partition);
      },
      initials(name) {
        let words = (name || '').trim().split(/\s+/).filter(Boolean);
        return words.length > 1 ? (words[0][0] + words[1][0]).toUpperCase() : (words[0] || '?').slice(0, 2).toUpperCase();
      },
      logoColor(name) {
        let sum = 0;
        for (let i = 0; i < (name || '').length; i++) {
          sum += name.charCodeAt(i);
        }
        return LOGO_COLORS[sum % LOGO_COLORS.length];
      },
      // Second line under the name: how it runs and through which contract
      subline(system) {
        let parts = [];
        if (system.runs_as) {
          parts.push(this.$t('systems_overview.runs_as.' + system.runs_as));
        }
        if (this.overview.modules.contracts && system.contracts.length) {
          parts.push(system.contracts.map(contract => contract.name).join(', '));
        }
        return parts.join(' · ');
      },
      where(system) {
        if (['premises', 'physical'].includes(system.runs_as)) {
          return system.location || this.$t('systems_overview.runs_in.no_location');
        }
        if (this.overview.modules.contracts) {
          return system.countries.length ? system.countries.join(', ') : this.$t('systems_overview.runs_in.no_contract');
        }
        return '';
      },
      // Stored as text levels ("Very low"…"Very high"), shown as 1–4
      ciaClass: impactClass,
      impactLevel: impactLevel,
      impactKey: impactKey,
      confirmedClass(system) {
        if (system.open_task) {
          return system.open_task.overdue ? 'is-late' : 'is-open';
        }
        return system.last_confirmed ? 'is-ok' : 'is-none';
      },
      confirmedText(system) {
        if (system.open_task) {
          return system.open_task.overdue ? this.$t('systems_overview.confirmed.overdue') : this.$t('systems_overview.confirmed.open');
        }
        return system.last_confirmed ? moment(system.last_confirmed).format('D MMM YYYY') : this.$t('systems_overview.confirmed.never');
      },
      confirmedSub(system) {
        if (system.open_task) {
          return system.open_task.project;
        }
        return system.last_confirmed ? '' : this.$t('systems_overview.confirmed.not_in_project');
      },
      missingTitle(system) {
        return system.missing.map(key => this.$t('systems_overview.missing.' + key)).join(', ');
      },
      ringDash(value, total, radius) {
        let circumference = 2 * Math.PI * radius;
        return (circumference * value / total) + ' ' + circumference;
      },
      setTab(key) {
        this.tab = key;
        this.remember();
      },
      setLens(key) {
        this.lens = key;
        this.remember();
      },
      remember() {
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify({ lens: this.lens }));
        } catch (e) {
          // Private windows can block storage; the page works without it
        }
      },
      openAttention(item) {
        if (item.route) {
          this.$router.push(item.route);
          return;
        }
        if (item.slug) {
          this.$router.push({ name: 'systems.view', params: { slug: item.slug } });
          return;
        }
        this.setTab(item.tab);
      },
      toggle(id, checked) {
        this.selected = checked ? _.uniq(this.selected.concat([id])) : this.selected.filter(item => item !== id);
      },
      toggleAll(checked) {
        let ids = this.selectableRows.map(system => system.id);
        this.selected = checked ? _.uniq(this.selected.concat(ids)) : this.selected.filter(id => !ids.includes(id));
      },
      loadUsers(query) {
        let self = this;
        piincHttp.get('users', { params: { client: this.getClient.id, keyword: query || undefined, per_page: 50 } }).then(function(response) {
          self.users = response.data.items;
        }, function() {});
      },
      loadTags(query) {
        let self = this;
        piincHttp.get('tags', { params: { client: this.getClient.slug, keyword: query || undefined } }).then(function(response) {
          self.tags = response.data;
        }, function() {});
      },
      addTag(name) {
        let tag = { name: name, name_translated: name };
        this.tags.push(tag);
        this.bulkValues.push(tag);
      },
      // The owner becomes owner; everyone else on the system stays as a contributor
      setOwner(system, user) {
        let self = this;
        let users = _.uniq(system.users.map(member => member.id).concat([user.id]));
        piincHttp.put('systems/' + system.slug, { users: users, owner: user.id }).then(function() {
          self.$toastr('success', self.$t('systems_overview.saved'));
          self.loadOverview();
        }, function() {});
      },
      setRunsAs(system, value) {
        let self = this;
        piincHttp.put('systems/' + system.slug, { runs_as: value }).then(function() {
          self.$toastr('success', self.$t('systems_overview.saved'));
          self.loadOverview();
        }, function() {});
      },
      openBulkModal(mode) {
        this.bulkMode = mode;
        this.bulkValues = [];
        if (mode === 'contributors') {
          this.loadUsers('');
        } else {
          this.loadTags('');
        }
        this.$refs['bulk-modal'].show();
      },
      submitBulkModal() {
        let values = this.bulkMode === 'contributors'
          ? { users: this.bulkValues.map(user => user.id) }
          : { tags: this.bulkValues.map(tag => tag.name_translated) };
        this.bulk(this.bulkMode, values);
        this.$refs['bulk-modal'].hide();
      },
      bulk(action, values) {
        let self = this;
        piincHttp.put('systems/bulk', Object.assign({ ids: this.selected, action: action }, values || {})).then(function(response) {
          let skipped = response.data.skipped.length;
          self.$toastr(skipped ? 'warning' : 'success', skipped
            ? self.$tc('systems_overview.bulk.skipped', skipped, { count: skipped })
            : self.$tc('systems_overview.bulk.done', response.data.updated.length, { count: response.data.updated.length }));
          self.selected = [];
          self.loadOverview();
        }, function() {});
      },
      startProject() {
        this.$refs.createProject.showCreateProjectModal();
      },
      focusSearch(event) {
        if ((event.metaKey || event.ctrlKey) && event.key && event.key.toLowerCase() === 'k') {
          event.preventDefault();
          this.$refs.search && this.$refs.search.focus();
        }
      }
    },
    created() {
      try {
        let saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
        if (saved.lens) {
          this.lens = saved.lens;
        }
      } catch (e) {
        // Ignore unreadable or blocked storage
      }
      this.loadOverview();
    },
    mounted() {
      window.addEventListener('keydown', this.focusSearch);
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(entries => {
          this.tableWidth = entries[0].contentRect.width;
        });
        this.resizeObserver.observe(this.$refs.table);
      }
      // /systems/create opens the create panel here, once the overview (and so the user's permissions) has loaded
      this.pendingCreate = !!this.$route.query.create;
    },
    beforeDestroy() {
      window.removeEventListener('keydown', this.focusSearch);
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
    }
  }
</script>
